<template>
  <div class="al-viewer-container">
    <!-- Filters panel -->
    <div class="filters">
      <!-- <label>
          Min Overall Score
          <input type="number" step="0.01" v-model.number="filters.minOverallScore" />
        </label>
        <label>
          Max Overall Score
          <input type="number" step="0.01" v-model.number="filters.maxOverallScore" />
        </label> -->
      <label>
        <!-- Site -->
        <input
          class="al-input"
          type="text"
          v-model="filters.site"
          placeholder="Site (e.g. Cadia)"
        />
      </label>
      <label>
        <!-- Datetime Start -->
        <!-- <input type="date" v-model="filters.startDatetime" /> -->
        <PrimeCalendar
          id="calendar-24h"
          v-model="filters.startDatetime"
          showTime
          hourFormat="24"
          showIcon
          :stepMinute="Number(15)"
          dateFormat="dd/mm/yy"
          placeholder="Start Datetime"
        />
      </label>
      <label>
        <!-- Datetime End -->
        <!-- <input type="date" v-model="filters.endDatetime" /> -->
        <PrimeCalendar
          id="calendar-24h"
          v-model="filters.endDatetime"
          showTime
          hourFormat="24"
          showIcon
          :stepMinute="Number(15)"
          dateFormat="dd/mm/yy"
          placeholder="End Datetime"
        />
      </label>
      <button class="al-button" @click="applyFilters">Apply</button>
    </div>

    <!-- Data table -->
    <div class="table-container">
      <table>
        <thead>
          <tr>
            <!-- ← New Action column header -->
            <th>Action</th>

            <!-- Data column headers -->
            <th
              v-for="col in tableColumns"
              :key="col.key"
              @click="setSort(col.key)"
              class="sortable"
            >
              {{ col.key }}
              <span v-if="sort.key === col.key">
                {{ sort.order === "asc" ? "↑" : "↓" }}
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, idx) in tableData" :key="idx">
            <td>
              <button @click="emitNavigate(row)">Go</button>
            </td>

            <td v-for="col in tableColumns" :key="col.key">
              <template v-if="row[col.key] == null"> - </template>
              <template v-else-if="col.type === 'date'">
                {{ new Date(row[col.key]).toLocaleString() }}
              </template>
              <template v-else>
                {{ row[col.key] }}
              </template>
            </td>
          </tr>
          <tr v-if="!loading && tableData.length === 0">
            <td :colspan="tableColumns.length + 1" class="no-data">
              No records found.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div class="pagination">
      <button @click="prevPage" :disabled="page === 1">« Prev</button>
      <span>Page {{ page }} of {{ totalPages }}</span>
      <button @click="nextPage" :disabled="page === totalPages">Next »</button>
      <label>
        Page Size
        <select v-model.number="pageSize" @change="applyFilters">
          <option v-for="n in [10, 20, 50, 100]" :key="n" :value="n">
            {{ n }}
          </option>
        </select>
      </label>
    </div>
  </div>
</template>

<script>
import { api_config } from "@/env.ts";
import axios from "axios";
import { getToken } from "@/keycloak-wrapper.ts";

export default {
  name: "ActiveLearningDatabaseViewer",
  emits: ["navigate"],
  data() {
    return {
      tableData: [],
      tableColumns: [],
      sort: { key: "OverallScore", order: "desc" },
      filters: {
        minOverallScore: null,
        maxOverallScore: null,
        site: "",
        startDatetime: "",
        endDatetime: "",
      },
      page: 1,
      pageSize: 10,
      total: 0,
      loading: false,
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.total / this.pageSize) || 1;
    },
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const token = await getToken();

        // build request body instead of query params
        const payload = {
          page: this.page,
          page_size: this.pageSize,
          sort_by: this.sort.key,
          sort_order: this.sort.order,
          min_overall_score: this.filters.minOverallScore,
          max_overall_score: this.filters.maxOverallScore,
          site: this.filters.site || undefined,
          start_datetime: this.filters.startDatetime
            ? new Date(this.filters.startDatetime).toISOString()
            : undefined,
          end_datetime: this.filters.endDatetime
            ? new Date(this.filters.endDatetime).toISOString()
            : undefined,
        };

        const resp = await axios.post(
          `${api_config.ml_backend}/active-learning/samples`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        this.tableData = resp.data.items;
        this.total = resp.data.total;

        if (!this.tableColumns.length && this.tableData.length) {
          // only include columns where at least one value is non-null
          const keys = Object.keys(this.tableData[0]);
          this.tableColumns = keys
            .filter((key) => this.tableData.some((row) => row[key] != null))
            .map((key) => {
              const sample = this.tableData.find((r) => r[key] != null)[key];
              let type = "string";
              if (typeof sample === "number") type = "number";
              else if (!isNaN(Date.parse(sample))) type = "date";
              return { key, type };
            });
        }
      } catch (err) {
        console.error("Error loading active learning data:", err);
      } finally {
        this.loading = false;
      }
    },
    applyFilters() {
      this.page = 1;
      this.fetchData();
    },
    setSort(columnKey) {
      if (this.sort.key === columnKey) {
        this.sort.order = this.sort.order === "asc" ? "desc" : "asc";
      } else {
        this.sort.key = columnKey;
        this.sort.order = "desc";
      }
      this.fetchData();
    },
    prevPage() {
      if (this.page > 1) {
        this.page--;
        this.fetchData();
      }
    },
    nextPage() {
      if (this.page < this.totalPages) {
        this.page++;
        this.fetchData();
      }
    },
    emitNavigate(row) {
      console.log(
        "navigating to:",
        row.Site,
        row.SentinexID,
        row.StartDatetime
      );
      this.$emit("navigate", row.Site, row.SentinexID, row.StartDatetime);
    },
  },
  mounted() {
    this.fetchData();
  },
};
</script>

<style scoped>
.al-viewer-container {
  display: grid;
  grid-template-rows: var(--control-height-sm) auto var(--control-height-sm);
  height: 100%;
  min-height: 0;
}
.filters {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-xs);
  /* margin-top: 6px; */
  padding: 0 var(--spacing-xs);
  height: var(--control-height-sm);
  width: 100%;
}
.filters label {
  display: flex;
  flex-direction: column;
  font-size: 0.9em;
}
.table-container {
  overflow: auto;
  height: 100%; /* Make sure the table container fills the remaining space */
}
table {
  width: calc(100% - 0.625rem);
  margin: 0 var(--spacing-xs);
  /* margin: 5px; */
  /* border-collapse: collapse;
    margin-bottom: 12px; */
}
thead th,
tbody td {
  border: 1px solid var(--colour-border-light);
}
thead th {
  padding: 4px;
  font-weight: 800;
}
thead th.sortable {
  cursor: pointer;
  background: #f4f4f4;
  user-select: none;
}
button {
  padding: var(--spacing-2xs) var(--spacing-md);
  margin: 0;
  height: var(--control-height-sm);
  font-size: var(--font-size-md);
  background-color: var(--color-primary);
  color: var(--colour-bg-light);
  border: 0;
  border-radius: var(--radius-md);
  cursor: pointer;
}
button:hover {
  background-color: var(--color-primary-hover);
}
.no-data {
  text-align: center;
  font-style: italic;
  color: #666;
}
.pagination {
  display: flex;
  align-items: center;
  margin-top: var(--spacing-xs);
  gap: var(--spacing-sm);
  height: var(--control-height-sm);
}
button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.al-input {
  font-size: var(--font-size-md);
  height: var(--control-height-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid rgb(203, 213, 225);
  border-radius: var(--radius-md);
  transition: 0.2s ease;
}
.al-input:hover {
  border: 1px solid rgb(148, 163, 184);
}
.al-input:focus {
  border: 1px solid var(--color-primary);
  box-shadow: 0 0 8px 2px rgba(0, 155, 105, 0.4);
  outline: 0;
  animation: pulse-shadow 0.2s ease-out forwards;
}
.al-button {
  font-size: var(--font-size-md);
  height: var(--control-height-sm);
  margin: 0;
  /* padding: var(--spacing-sm) var(--spacing-md); */
  background: var(--color-primary);
  border: 1px solid rgb(203, 213, 225);
  color: var(--colour-bg-light);
  border-radius: var(--radius-md);
  transition: 0.2s ease;
  cursor: pointer;
}
.al-button:hover {
  background-color: var(--color-primary-hover);
}
@keyframes pulse-shadow {
  0% {
    box-shadow: 0 0 0 0 rgba(0, 155, 105, 1);
  }
  50% {
    box-shadow: 0 0 1px 1px rgba(60, 155, 105, 1);
  }
  100% {
    box-shadow: 0 0 0px 0px rgba(0, 155, 105, 1);
  }
}
::v-deep(.p-component input) {
  font-size: var(--font-size-md);
  height: var(--control-height-sm);
  /* padding: 8px 12px; */
  /* border: 1px solid rgb(203, 213, 225); */
  /* border-top-left-radius: 6px;
    border-bottom-left-radius: 6px; */
  transition: 0.2s ease;
}
::v-deep(.p-datepicker-trigger) {
  height: var(--control-height-sm);
  width: var(--icon-size-md);
}
</style>
